/**
 * Workspace wire contracts: CreateFormRequest and FormSummary.
 *
 * AUTHORITY: The Pydantic models in `dashboard/features/forms/api/response_models.py`
 * are the sole wire-schema authority. These Zod schemas are CHECKED CONSUMERS
 * of the generated JSON Schema files in `../generated/`. They MUST be kept in
 * sync via the generator — never edited as an independent authority.
 *
 * Both schemas are `.strict()` so unknown wire fields fail visibly rather than
 * being silently stripped. Forms frontend and backend deploy together; future
 * fields require an intentional schema update, regenerated artifacts, and
 * compatibility tests.
 */

import { z } from 'zod';
import { UuidSchema } from './ids';
import { FormDefinitionSchema, FormStatusSchema } from './definition';
import { CommandBatchSchema } from './commands';

export const CreateFormRequestSchema = z
  .object({
    title: z.string().max(500).nullable().optional(),
    creation_source: z.literal('blank'),
  })
  .strict();

export type CreateFormRequest = z.infer<typeof CreateFormRequestSchema>;

export const FormSummarySchema = z
  .object({
    form_id: UuidSchema,
    title: z.string(),
    status: FormStatusSchema,
    draft_version: z.number().int().nonnegative(),
    current_published_revision_id: z.string().nullable(),
    updated_at: z.string().refine((value) => !Number.isNaN(Date.parse(value)), {
      message: 'Expected an ISO date-time string',
    }),
    submission_count: z.number().int().nonnegative(),
  })
  .strict();

export type FormSummary = z.infer<typeof FormSummarySchema>;

export const FormAgentConversationBindingSchema = z
  .object({
    conversation_id: z.string().max(36).nullable(),
  })
  .strict();

export type FormAgentConversationBinding = z.infer<typeof FormAgentConversationBindingSchema>;

export const AgentApplyRequestSchema = z
  .object({
    application_token: z.string().min(1).max(128),
    settlement_token: z.string().min(1).max(500),
    change_id: z.string().min(1).max(128),
    turn_id: z.string().min(1).max(128),
    command_batch: CommandBatchSchema,
  })
  .strict();

export type AgentApplyRequest = z.infer<typeof AgentApplyRequestSchema>;

const ReducerRejectionSchema = z
  .object({
    code: z.enum([
      'schema_validation_failed',
      'form_id_mismatch',
      'version_conflict',
      'unknown_reference',
      'reference_conflict',
      'duplicate_id',
      'logic_cycle',
      'logic_type_mismatch',
      'invalid_navigation',
      'required_path_unreachable',
      'operation_integrity_conflict',
      'application_service_required',
    ]),
    message: z.string(),
    command_index: z.number().int().nullable(),
    field_path: z.string(),
  })
  .strict();

export const AgentApplyResponseSchema = z
  .object({
    accepted: z.boolean(),
    definition: FormDefinitionSchema.nullable(),
    rejection: ReducerRejectionSchema.nullable(),
    new_draft_version: z.number().int().nullable(),
    operation_fingerprint: z.string().nullable(),
    settlement_acknowledged: z.boolean(),
  })
  .strict();

export type AgentApplyResponse = z.infer<typeof AgentApplyResponseSchema>;
